// File: LogVisitsProvider.tsx

import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { UserDetailsViewModel } from "../../configuration/api/apiSchema";
import { api, ENV_WEB_API_BASEPATH } from "../../configuration/api/apiHelper";
import { AuthData } from "../../pages/Auth/reducer";

// ========== Props from Redux state ==========
interface StateFromProps {
    userDetails?: UserDetailsViewModel;
    authData?: AuthData;
}

// ========== Optional extra props passed in ==========
interface PassedProps { }

// ========== Combined props for the component ==========
type Props = StateFromProps & PassedProps;

/**
 * Logs site visits on each route change, including user info & IP address.
 *
 * This component calls ipify to fetch the client's public IP, then calls
 * your internal endpoint (via Swagger's `api.activityLog.activityLogCreate`).
 * 
 * We do NOT use useApi() here to avoid the React Hooks rule error, so
 * we just make direct async calls.
 */
const LogVisitsProvider: React.FC<Props> = ({ userDetails }) => {
    const location = useLocation();

    useEffect(() => {
        async function logVisit() {
            let ip: string = "";

            // 1. Fetch user's IP from ipify
            try {
                const res = await fetch("https://api.ipify.org?format=json");
                const data = await res.json();
                ip = data.ip;
            } catch (error) {
                console.error("Failed to fetch IP address:", error);
            }

            // 2. Prepare the log data
            const logData = {
                url: window.location.href,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                ipAddress: ip,
                data: userDetails ? JSON.stringify(userDetails) : "none"
            };

            // 3. Call your Swagger-based endpoint
            try {
                const response = await api.activityLog.activityLogCreate(logData);

                // If the swagger call doesn't return a "result" success, optionally fallback:
                if (response.error) {
                    console.error("activityLogCreate threw an error:", response.error);
                }
            } catch (err) {
                console.error("activityLogCreate threw an error:", err);
                // OPTIONAL fallback (uncomment if needed):
                /*
                try {
                  await fetch(`${ENV_WEB_API_BASEPATH}/activity-log`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(logData),
                  });
                } catch (fallbackErr) {
                  console.error("Fallback fetch also failed:", fallbackErr);
                }
                */
            }
        }

        logVisit();
    }, [location, userDetails]);

    return null; // This component doesn't render anything
};

// ========== Redux State Mapping ==========
const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
    userDetails: state.user.userDetails,
    authData: state.user.authData,
});

// Connect to Redux (no additional dispatch needed here)
export default connect(mapStateToProps)(LogVisitsProvider);
